import { useState } from 'react';
import { ComplianceStub } from '../../types';
import { useCompliancePackStore } from '../../stores/compliancePackStore';
import { Button } from '../ui/button';

interface StubCardProps {
  stub: ComplianceStub;
}

export function StubCard({ stub }: StubCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(stub.ears_text);
  // Plan §1.2 acceptance criterion #2: every drop must be accompanied
  // by an architect-supplied justification logged into ``dropped[].reason``.
  const [isDropping, setIsDropping] = useState(false);
  const [dropReason, setDropReason] = useState(stub.inheritance_notes ?? '');
  const updateStubStatus = useCompliancePackStore(state => state.updateStubStatus);
  const setFocusedStub = useCompliancePackStore(state => state.setFocusedStub);

  const handleAccept = () => {
    updateStubStatus(stub.stub_id, 'user_confirmed');
  };

  const handleDropStart = () => {
    setIsDropping(true);
  };

  const handleDropConfirm = () => {
    if (!dropReason.trim()) return;
    // The justification is persisted in ``inheritance_notes`` on the
    // stub itself so the SRD merger and exporter can both surface it
    // when the architect later asks "why did we skip this control?".
    // The Phase 5 traceability matrix's ``compliance_pack_id`` column
    // will pick it up via the dropped[] entry written by the backend.
    updateStubStatus(stub.stub_id, 'dropped', undefined, dropReason.trim());
    setIsDropping(false);
  };

  const handleDropCancel = () => {
    setIsDropping(false);
    setDropReason(stub.inheritance_notes ?? '');
  };

  const handleSaveEdit = () => {
    updateStubStatus(stub.stub_id, 'user_confirmed', editedText);
    setIsEditing(false);
  };

  const handleStartEdit = () => {
    setEditedText(stub.ears_text);
    setIsEditing(true);
  };

  // Re-open a previously accepted/dropped row for re-review. The architect
  // should always be able to revisit a decision — clicking "Change…" puts
  // the card back into the pending state so the Accept / Edit / Drop
  // buttons render again.
  const handleReopen = () => {
    updateStubStatus(stub.stub_id, 'drafted');
  };

  const statusColors: Record<ComplianceStub['status'], string> = {
    pending: 'bg-amber-500/15 text-amber-300 border border-amber-500/30',
    drafted: 'bg-sky-500/15 text-sky-300 border border-sky-500/30',
    user_confirmed: 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30',
    dropped: 'bg-slate-500/15 text-slate-300 border border-slate-500/30',
    inherited: 'bg-purple-500/15 text-purple-300 border border-purple-500/30',
    shared: 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30',
  };

  const isResolved = stub.status === 'user_confirmed' || stub.status === 'dropped';
  const isPendingReview = stub.status === 'pending' || stub.status === 'drafted';

  return (
    <div
      className="border border-slate-700/60 rounded-md p-4 mb-2 shadow-sm bg-slate-900/40 cursor-pointer transition-colors hover:bg-slate-900/60"
      onClick={() => setFocusedStub(stub.stub_id)}
      data-testid="gate-25-row"
    >
      <div className="flex justify-between items-start mb-2 gap-3">
        <h4 className="font-semibold text-base text-slate-100 min-w-0">
          {stub.stub_id} — {stub.capability}
        </h4>
        <span
          className={`px-2 py-1 rounded text-xs font-medium shrink-0 ${
            statusColors[stub.status] ?? 'bg-slate-500/15 text-slate-300 border border-slate-500/30'
          }`}
        >
          {stub.status}
        </span>
      </div>

      <div className="mb-3">
        {isEditing ? (
          <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
            <textarea
              data-testid="gate-25-edit-text"
              className="w-full bg-slate-950/40 border border-slate-700 rounded px-2 py-1 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              rows={3}
              autoFocus
            />
            <div className="flex space-x-2">
              <Button onClick={handleSaveEdit} size="sm">Save</Button>
              <Button variant="outline" onClick={() => setIsEditing(false)} size="sm">Cancel</Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-300 leading-relaxed">{stub.ears_text}</p>
        )}
      </div>

      {isDropping && (
        <div
          className="space-y-2 mb-3 p-3 rounded border border-amber-700/40 bg-amber-950/20"
          onClick={(e) => e.stopPropagation()}
        >
          <label className="text-xs font-semibold text-amber-300">
            Justification for dropping (logged in audit trail){' '}
            <span className="text-rose-400">*</span>
          </label>
          <textarea
            data-testid="gate-25-rationale"
            className="w-full bg-slate-950/40 border border-amber-700/40 rounded px-2 py-1 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-amber-500/50"
            value={dropReason}
            onChange={(e) => setDropReason(e.target.value)}
            placeholder="e.g. 'Out of scope — handled by upstream identity provider per charter.'"
            rows={2}
            autoFocus
          />
          <div className="flex space-x-2">
            <Button
              onClick={handleDropConfirm}
              size="sm"
              variant="destructive"
              disabled={!dropReason.trim()}
            >
              Confirm drop
            </Button>
            <Button onClick={handleDropCancel} size="sm" variant="outline">
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Action row — always visible while not in an inline editor.
          Pending/drafted rows show the canonical Accept / Edit / Drop
          triplet. Resolved rows (user_confirmed / dropped) show the
          *same* affordances plus a Re-open button so the architect can
          revisit a previous decision instead of being locked out. */}
      {!isEditing && !isDropping && (
        <div
          className="flex flex-wrap gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          {isPendingReview && (
            <>
              <Button data-testid="gate-25-approve" onClick={handleAccept} size="sm" variant="default">
                Accept
              </Button>
              <Button data-testid="gate-25-modify" onClick={handleStartEdit} size="sm" variant="outline">
                Edit
              </Button>
              <Button data-testid="gate-25-reject" onClick={handleDropStart} size="sm" variant="destructive">
                Drop
              </Button>
            </>
          )}

          {isResolved && (
            <>
              <Button onClick={handleStartEdit} size="sm" variant="outline">
                Edit text
              </Button>
              {stub.status === 'user_confirmed' ? (
                <Button onClick={handleDropStart} size="sm" variant="destructive">
                  Drop instead
                </Button>
              ) : (
                <Button onClick={handleAccept} size="sm" variant="default">
                  Accept instead
                </Button>
              )}
              <Button onClick={handleReopen} size="sm" variant="outline">
                Re-open for review
              </Button>
            </>
          )}
        </div>
      )}

      {stub.status === 'dropped' && stub.inheritance_notes && (
        <div className="mt-3 text-xs text-slate-400 italic border-l-2 border-slate-600 pl-2">
          Dropped — {stub.inheritance_notes}
        </div>
      )}

      {stub.confidence !== undefined && stub.confidence !== null && (
        <div className="mt-2 text-xs text-slate-500">
          Confidence: {(stub.confidence * 100).toFixed(0)}%
        </div>
      )}
    </div>
  );
}
